"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Apple, Scale, Activity, ArrowRight, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const nutritionHighlights = [{
  icon: Scale,
  title: "Macronutrient balance",
  description: "Optimal protein, healthy lipid profiles, and digestible carbohydrates tailored to metabolic needs."
}, {
  icon: Activity,
  title: "Life-stage requirements",
  description: "Formulas for growth, adult maintenance, and senior vitality supported by clinical standards."
}, {
  icon: Apple,
  title: "Ingredient verification",
  description: "Independent veterinary analysis of whole foods, safe additions, and common dietary allergens."
}];
export default function Intro() {
  const shouldReduceMotion = useReducedMotion();
  const fadeInVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const staggerContainer = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  return <section id="intro" data-nav="light" className="bg-background text-foreground py-12 md:py-20 lg:py-24 border-b border-border overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Main Editorial Content Column */}
          <motion.div className="lg:col-span-7 flex flex-col items-start" initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={staggerContainer}>
            {/* Category Eyebrow */}
            <motion.div variants={fadeInVariants} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-foreground text-xs font-semibold uppercase tracking-wider mb-5">
              <Sparkles className="size-3.5 text-chart-1" />
              <span>Evidence-based clinical guides</span>
            </motion.div>

            {/* Main Headline */}
            <motion.h1 variants={fadeInVariants} className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground leading-[1.15] mb-6">
              Pet Nutrition
            </motion.h1>

            {/* Editorial Lead Paragraph */}
            <motion.p variants={fadeInVariants} className="font-default text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-2xl mb-8">
              Clear, evidence-backed feeding guides, nutrient breakdowns, and life-stage dietary
              recommendations to help your dog or cat thrive.
            </motion.p>

            {/* Clinical Governance Callout Motif */}
            <motion.div variants={fadeInVariants} className="w-full bg-card rounded-r-xl border border-border border-l-4 border-l-chart-1 p-5 sm:p-6 mb-8 shadow-xs">
              <div className="flex items-start gap-3.5">
                <ShieldCheck className="size-5 sm:size-6 text-chart-1 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <h3 className="font-default font-semibold text-sm sm:text-base text-card-foreground">
                    Veterinary Nutrition Standard
                  </h3>
                  <p className="font-default text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    Every article and dietary formulation on PetsWhirl is reviewed against current
                    veterinary clinical trials and nutritional research.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Actions */}
            <motion.div variants={fadeInVariants} className="flex flex-wrap items-center gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="w-full sm:w-auto shadow-sm">
                <Link to="/PetHealthPage" className="inline-flex items-center gap-2">
                  <span>Explore health topics</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto hover:bg-secondary text-foreground">
                <Link to="/ProductsReviewsPage">Dietary gear & food reviews</Link>
              </Button>
            </motion.div>
          </motion.div>

          {/* Visual Showcase Column */}
          <motion.div className="lg:col-span-5 relative" initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeInVariants}>
            <div className="relative mx-auto max-w-md lg:max-w-none">
              {/* Image Frame with soft editorial aperture */}
              <div className="relative rounded-2xl overflow-hidden border border-border bg-card shadow-lg">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/pet_nutrition_balanced_ingredients.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/pet_nutrition_balanced_ingredients.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/pet_nutrition_balanced_ingredients.webp-wvc-sizes" alt="Fresh balanced pet food ingredients alongside healthy companion pets resting" className="w-full h-[360px] sm:h-[420px] object-cover object-center transition-transform duration-500 hover:scale-[1.02]" loading="lazy" decoding="async" />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/40 via-transparent to-transparent pointer-events-none" />

                {/* Floating Bottom Card Over Image */}
                <div className="absolute bottom-4 left-4 right-4 bg-background/95 backdrop-blur-xs rounded-xl p-4 border border-border/80 shadow-md">
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                    <span className="font-semibold text-foreground">Dietary Focus</span>
                    <span className="text-chart-1 font-medium">Daily Wellness</span>
                  </div>
                  <p className="font-default text-xs text-muted-foreground leading-normal">
                    Precision feeding plans adjusted for age, breed metabolism, and daily activity.
                  </p>
                </div>
              </div>

              {/* Decorative Subtle Accent Badge */}
              <div className="hidden sm:flex absolute -top-4 -right-4 bg-card border border-border rounded-xl px-4 py-2.5 shadow-sm items-center gap-2">
                <div className="size-2 rounded-full bg-chart-1" />
                <span className="font-default text-xs font-semibold text-card-foreground">
                  Evidence-Informed Care
                </span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Highlight Metrics / Three-Pillar Grid */}
        <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14 pt-12 border-t border-border" initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} variants={staggerContainer}>
          {nutritionHighlights.map((item, index) => {
          const IconComponent = item.icon;
          return <motion.div key={item.title} data-index={index} variants={fadeInVariants} className="bg-card rounded-xl border border-border p-6 transition-[box-shadow,border-color] duration-300 hover:shadow-md hover:border-border/80">
                <div className="size-10 rounded-lg bg-secondary text-foreground flex items-center justify-center mb-4 border border-border">
                  <IconComponent className="size-5 text-chart-1" />
                </div>
                <h2 className="font-serif text-lg font-semibold text-card-foreground mb-2">
                  {item.title}
                </h2>
                <p className="font-default text-sm text-muted-foreground leading-relaxed">
                  {item.description}
                </p>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}